import React, { useState, useEffect, useCallback } from "react";
import { ButtonH } from "components/UiKit/ButtonH";
import { Ico } from "components/Uikit";
import { useAudioStateContext } from "hooks/useAudioStateContext";
import { useAudioHandlesContext } from "hooks/useAudioHandlesContext";
import { forceAlignFragmentsApi, forceAlignPreviewApi, undoResyncApi, resyncUndoInfoApi, ResyncUndoInfoResponse } from "api/fastApi";
import { message, Modal, Table, Tag, Popconfirm, Tooltip } from "antd";
import { MetadataToSubtitlesLanguages } from "providers/AudioProvider/types";
import { useIsEnglishUi } from "utils/uiLanguage";
import { AutoEditGate, useAutoEditBlock } from "../../../ManualOnly/AutoEditGate";

const spinKeyframes = `
@keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}
`;

interface AffectedFragment {
    id: string;
    captions: string;
    current_start_ms: number;
    original_start_ms: number;
    diff_ms: number;
    adjusted_start_ms?: number;
    adjusted_diff_ms?: number;
    pushed_by_audio?: boolean;
}

const formatMs = (ms: number): string => {
    const totalSec = ms / 1000;
    const min = Math.floor(totalSec / 60);
    const sec = (totalSec % 60).toFixed(1);
    return `${min}:${sec.padStart(4, "0")}`;
};

const columns = [
    {
        title: "ID",
        dataIndex: "id",
        key: "id",
        width: 100,
    },
    {
        title: "Текст",
        dataIndex: "captions",
        key: "captions",
        ellipsis: true,
    },
    {
        title: "Текущий start",
        dataIndex: "current_start_ms",
        key: "current_start_ms",
        width: 120,
        render: (val: number) => formatMs(val),
    },
    {
        title: "Оригинальный start",
        dataIndex: "original_start_ms",
        key: "original_start_ms",
        width: 140,
        render: (val: number) => formatMs(val),
    },
    {
        title: "Разница",
        key: "adjusted_diff_ms",
        width: 180,
        render: (_: unknown, record: AffectedFragment) => {
            const val = record.adjusted_diff_ms ?? record.diff_ms;
            const color = val === 0 ? "green" : val > 0 ? "red" : "blue";
            const sign = val > 0 ? "+" : "";
            return (
                <span>
                    <Tag color={color}>{sign}{val.toFixed(0)} мс</Tag>
                    {record.pushed_by_audio && (
                        <Tag color="warning" style={{ marginLeft: 4, fontSize: 11 }}>↓ длина аудио</Tag>
                    )}
                </span>
            );
        },
    },
];

export const ResyncAudio: React.FC = () => {
    const state = useAudioStateContext();
    const handles = useAudioHandlesContext();
    const [loading, setLoading] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [affectedFragments, setAffectedFragments] = useState<AffectedFragment[]>([]);
    const [fragmentIdsToResync, setFragmentIdsToResync] = useState<string[] | undefined>(undefined);

    // Undo state
    const [undoInfo, setUndoInfo] = useState<ResyncUndoInfoResponse | null>(null);
    const [undoLoading, setUndoLoading] = useState(false);

    const fromEng = useIsEnglishUi();
    // Двигает расставленную руками озвучку по исходной шкале — автоправка. Откат прошлого
    // ресинка — возврат к прежнему, он остаётся.
    const autoEdit = useAutoEditBlock();
    const labels = {
        syncTransBtn: fromEng ? "Resync" : "Выровнять",
        syncTransHint: fromEng
            ? "Resync timings in the right column (translated voiceover). It aligns selected fragments (or all fragments if nothing is selected) to the source timeline and fixes drift between subtitles and voiceover."
            : "Ресинк таймингов в правой колонке (переведённая озвучка). Выравнивает выбранные фрагменты (или все, если ничего не выбрано) по исходной временной шкале и исправляет рассинхрон между субтитрами и озвучкой.",
        errorData: fromEng ? "Missing data for resync" : "Не хватает данных для ресинка",
        errorPreview: fromEng ? "Failed to get preview" : "Не удалось получить предварительный просмотр",
        errorResync: fromEng ? "Resync failed" : "Ошибка ресинка",
        successResync: fromEng ? "Resync complete, reloading..." : "Ресинк завершён, загрузка данных...",
        successRefresh: fromEng ? "Data refreshed!" : "Данные обновлены!",
        previewTitle: fromEng ? "Resync Preview" : "Предварительный просмотр ресинка",
        previewSelected: fromEng ? "Selected:" : "Выбрано:",
        previewAll: fromEng ? "All fragments" : "Все фрагменты",
        execResync: fromEng ? "Execute resync" : "Выполнить ресинк",
        cancel: fromEng ? "Cancel" : "Отмена",
        totalFragments: fromEng ? "Total fragments:" : "Всего фрагментов:",
        willBeChanged: fromEng ? "Will be changed:" : "Будут изменены:",
        inSync: fromEng ? "All in sync" : "Все в синхронизации",
        undoBtn: fromEng ? "Undo resync" : "Откатить ресинк",
        undoConfirm: fromEng ? "Restore timings from before the last resync?" : "Восстановить тайминги до последнего ресинка?",
        undoSuccess: fromEng ? "Resync undone, reloading..." : "Ресинк откачен, загрузка данных...",
        undoError: fromEng ? "Failed to undo resync" : "Ошибка отката ресинка",
    };

    const checkUndoAvailability = useCallback(async () => {
        if (!state.mediaId || !state.folder || !state.lang) return;
        try {
            const info = await resyncUndoInfoApi({
                mediaId: state.mediaId,
                folderNum: parseInt(state.folder.toString(), 10),
                lang: state.lang,
                language_from: MetadataToSubtitlesLanguages[state.originalLang],
            });
            setUndoInfo(info);
        } catch {
            setUndoInfo(null);
        }
    }, [state.mediaId, state.folder, state.lang, state.originalLang]);

    useEffect(() => {
        checkUndoAvailability();
    }, [checkUndoAvailability]);

    const selectedTracks = state.selectedTranslatedTrackIds;
    const hasSelection = selectedTracks.length > 0;

    const handleResyncClick = async () => {
        if (autoEdit.blocked) return;
        if (!state.mediaId || !state.folder || !state.lang) {
            message.error(labels.errorData);
            return;
        }

        const ids = state.selectedTranslatedTrackIds.length
            ? state.selectedTranslatedTrackIds.map(s => s.track.original_id || s.track.id)
            : undefined;
        setFragmentIdsToResync(ids);
        setPreviewLoading(true);

        try {
            const preview = await forceAlignPreviewApi({
                mediaId: state.mediaId,
                folderNum: parseInt(state.folder.toString(), 10),
                lang: state.lang,
                language_from: MetadataToSubtitlesLanguages[state.originalLang],
                fragment_ids: ids,
            });

            setAffectedFragments(preview.affected);
            setShowPreview(true);
        } catch (error) {
            console.error("Preview error:", error);
            message.error(labels.errorPreview);
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleConfirmResync = async () => {
        setShowPreview(false);
        if (autoEdit.blocked) return;
        setLoading(true);

        try {
            await forceAlignFragmentsApi({
                mediaId: state.mediaId,
                folderNum: parseInt(state.folder.toString(), 10),
                lang: state.lang,
                language_from: MetadataToSubtitlesLanguages[state.originalLang],
                fragment_ids: fragmentIdsToResync,
            });

            message.success(labels.successResync);
            await handles.refreshAllSubtitles();
            message.success(labels.successRefresh);
            handles.setSelectedOriginalTrackIds([]);
            handles.setSelectedTranslatedTrackIds([]);
            await checkUndoAvailability();
        } catch (error) {
            console.error("Resync error:", error);
            message.error(labels.errorResync);
        } finally {
            setLoading(false);
        }
    };

    const handleUndoResync = async () => {
        if (!state.mediaId || !state.folder || !state.lang) return;

        setUndoLoading(true);
        try {
            await undoResyncApi({
                mediaId: state.mediaId,
                folderNum: parseInt(state.folder.toString(), 10),
                lang: state.lang,
                language_from: MetadataToSubtitlesLanguages[state.originalLang],
            });

            message.success(labels.undoSuccess);
            await handles.refreshAllSubtitles();
            message.success(labels.successRefresh);
            setUndoInfo(null);
        } catch (error) {
            console.error("Undo resync error:", error);
            message.error(labels.undoError);
        } finally {
            setUndoLoading(false);
        }
    };

    const changedCount = affectedFragments.filter((f) => (f.adjusted_diff_ms ?? f.diff_ms) !== 0).length;

    const undoTooltip = undoInfo?.available
        ? `${labels.syncTransBtn}, ${undoInfo.fragment_count} фр., ${undoInfo.timestamp ? new Date(undoInfo.timestamp).toLocaleTimeString() : ""}`
        : "";

    const helpIconStyle: React.CSSProperties = {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "1.1em",
        height: "1.1em",
        borderRadius: "50%",
        border: "1px solid #888",
        fontSize: "0.75em",
        lineHeight: 1,
        cursor: "help",
        userSelect: "none",
    };

    return (
        <>
            <style>{spinKeyframes}</style>
            <div className="_f _fm" style={{ gap: 8 }}>
                <div className="_f _fm" style={{ gap: 6 }}>
                    <AutoEditGate {...autoEdit}>
                        <ButtonH sm ghost onClick={handleResyncClick} disabled={loading || previewLoading || autoEdit.blocked}>
                            <Ico
                                size={"16"}
                                style={loading || previewLoading ? { animation: "spin 1s linear infinite", display: "inline-block" } : undefined}
                            >sync</Ico>
                            {labels.syncTransBtn}{state.selectedTranslatedTrackIds.length > 0 ? ` (${state.selectedTranslatedTrackIds.length})` : ""}
                        </ButtonH>
                    </AutoEditGate>
                    <Tooltip title={labels.syncTransHint}>
                        <span style={helpIconStyle}>?</span>
                    </Tooltip>
                </div>
                {undoInfo?.available && (
                    <Popconfirm
                        title={labels.undoConfirm}
                        description={undoTooltip}
                        onConfirm={handleUndoResync}
                        okText={labels.undoBtn}
                        cancelText={labels.cancel}
                    >
                        <ButtonH sm ghost disabled={undoLoading} style={{ color: "#faad14" }}>
                            <Ico
                                size={"16"}
                                style={undoLoading ? { animation: "spin 1s linear infinite", display: "inline-block" } : undefined}
                            >undo</Ico>
                            {labels.undoBtn}
                        </ButtonH>
                    </Popconfirm>
                )}
            </div>

            <Modal
                title={
                    <span>
                        {labels.previewTitle} ({labels.syncTransBtn})
                        {hasSelection && (
                            <Tag color="blue" style={{ marginLeft: 8 }}>{labels.previewSelected} {selectedTracks.length}</Tag>
                        )}
                        {!hasSelection && (
                            <Tag color="orange" style={{ marginLeft: 8 }}>{labels.previewAll}</Tag>
                        )}
                    </span>
                }
                open={showPreview}
                onOk={handleConfirmResync}
                onCancel={() => setShowPreview(false)}
                okText={labels.execResync}
                cancelText={labels.cancel}
                width={800}
                okButtonProps={{ danger: changedCount > 0 }}
            >
                <div style={{ marginBottom: 12 }}>
                    <span>{labels.totalFragments} <strong>{affectedFragments.length}</strong></span>
                    {changedCount > 0 && (
                        <span style={{ marginLeft: 16 }}>{labels.willBeChanged} <Tag color="red">{changedCount}</Tag></span>
                    )}
                    {changedCount === 0 && (
                        <span style={{ marginLeft: 16 }}><Tag color="green">{labels.inSync}</Tag></span>
                    )}
                </div>
                <Table
                    dataSource={affectedFragments}
                    columns={columns}
                    rowKey="id"
                    size="small"
                    pagination={false}
                    scroll={{ y: 400 }}
                    rowClassName={(record) => (record.adjusted_diff_ms ?? record.diff_ms) !== 0 ? "ant-table-row-warning" : ""}
                />
            </Modal>
        </>
    );
};
